'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { TextButton } from '@flipster/react/design-system/text-button';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, grid } from '@flipster/styled-system/patterns';

import { kolInsightsQueryParam, takeMarketInsights } from '../helpers';
import { kolInsightsBunja } from '../state';
import { KolInsightsEmptyState } from '../ui/empty-state';
import { KolInsightCard } from '../ui/insight-card';

interface KolInsightsMarketSectionProps {
  css?: Styles;
}

export const KolInsightsMarketSection = ({ css: cssProps }: KolInsightsMarketSectionProps) => {
  const { shouldShowMarketSectionAtom, insightsAtom, isEmptyAtom } = useBunja(kolInsightsBunja);
  const shouldShow = useAtomValue(shouldShowMarketSectionAtom);
  const insights = useAtomValue(insightsAtom);
  const isEmpty = useAtomValue(isEmptyAtom);

  if (!shouldShow) return null;

  const marketInsights = takeMarketInsights(insights);
  const showSeeAll = insights.length > 0;

  return (
    <section className={css(cssProps)}>
      <div className={flex({ justify: 'space-between', align: 'flex-end', mb: 16 })}>
        <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5' })}>
          <FormattedMessage id='Home.r260831.KOL_insight' />
        </h2>
        {showSeeAll && (
          <TextButton
            as='a'
            href={`/kol-insights?${kolInsightsQueryParam}=0`}
            label={<FormattedMessage id='Common.r250919.See_all' />}
            color='neutral'
          />
        )}
      </div>

      {isEmpty ? (
        <KolInsightsEmptyState isSmall />
      ) : (
        <div
          className={grid({
            columns: { base: 1, tablet: 3 },
            gap: 16,
            alignItems: 'stretch',
            '& > *:not(:first-child)': { display: 'none' },
            tablet: { '& > *:not(:first-child)': { display: 'flex' } },
          })}
        >
          {marketInsights.map((insight, index) => (
            <KolInsightCard
              key={`${insight.title}-${index}`}
              insight={insight}
              href={`/kol-insights?${kolInsightsQueryParam}=${index}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
